import { CONFIG, TOP_TIER, BOTTOM_TIER, tierName, type Movement } from '@h2h/shared';
import { TierBadge } from '../ui/bits';
import { ChevronUp, ChevronDown, Minus } from '../ui/icons';
import { cap, ordinalSuffix, signedPoints } from '../../lib/format';
import type { Zone } from '../../lib/round';
import { cn } from '../../lib/cn';

/** Tier-tinted glow stops for the dark hero (keyed to the tier badge palette). */
const TIER_GLOW = [
  'rgba(184,115,51,0.30)', // Bronze
  'rgba(174,180,191,0.26)', // Silver
  'rgba(227,168,43,0.30)', // Gold
  'rgba(143,183,196,0.28)', // Platinum
  'rgba(90,166,230,0.30)', // Diamond
  'rgba(126,91,214,0.32)' // Elite
] as const;

/**
 * The Division hero — a dark, self-explaining header for the live round board.
 *
 * It answers the three questions a player has the moment they land here:
 *  • WHAT am I looking at?  → tier badge + "Bronze · Group 1" + round + size
 *  • WHERE do I stand?      → big live rank, my points, and a zone status pill
 *  • WHY does it matter?    → the stakes bar (top N promote / bottom N relegate)
 *
 * Replaces the old all-white `DivisionStrip` so the board no longer reads as a
 * flat sheet of white cards.
 */
export function DivisionHero({
  tierIndex,
  groupLabel,
  playerCount,
  roundLabel,
  myRank,
  myScore,
  myZone,
  myMovement,
  promoteCount,
  relegateCount,
  live = false,
  liveProgress = null
}: {
  tierIndex: number;
  /** e.g. "Group 1" */
  groupLabel: string;
  playerCount: number;
  roundLabel: string;
  /** The player's live rank in the group, or null when they have no entry/score yet. */
  myRank: number | null;
  myScore: number | null;
  myZone: Zone | null;
  /** Set once the round has settled — overrides the live zone for the status pill. */
  myMovement: Movement | null;
  promoteCount: number;
  relegateCount: number;
  /** Round still in play — shows the "fresh group" marker and (with liveProgress)
   *  the provisional banner, so a live ranking is never mistaken for the final one. */
  live?: boolean;
  /** Fixtures settled vs total this round (for the "x/n matches in" live banner). */
  liveProgress?: { settled: number; total: number } | null;
}) {
  const tier = Math.max(BOTTOM_TIER, Math.min(TOP_TIER, tierIndex));
  const tierCount = CONFIG.TIERS.length;
  const here = cap(tierName(tier));
  const up = cap(tierName(Math.min(TOP_TIER, tier + 1)));
  const down = cap(tierName(Math.max(BOTTOM_TIER, tier - 1)));
  const atTop = tier >= TOP_TIER;
  const atBottom = tier <= BOTTOM_TIER;

  const status = statusFor(myZone, myMovement);
  const provisional =
    live && !!liveProgress && liveProgress.total > 0 && liveProgress.settled < liveProgress.total;

  return (
    <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] p-[17px] shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)]">
      <span
        aria-hidden
        className="pointer-events-none absolute -right-[44px] -top-[52px] h-[185px] w-[185px] rounded-full"
        style={{ background: `radial-gradient(circle, ${TIER_GLOW[tier] ?? TIER_GLOW[0]}, transparent 70%)` }}
      />

      <div className="relative">
        {/* identity */}
        <div className="flex items-center gap-3">
          <TierBadge tierIndex={tier} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-[15px] font-extrabold tracking-snug text-white">
                {here} · {groupLabel}
              </span>
              {live && (
                <span className="shrink-0 rounded-full bg-white/[0.13] px-[7px] py-[2px] text-[8.5px] font-extrabold uppercase tracking-[0.05em] text-white/75">
                  New group
                </span>
              )}
            </div>
            <div className="num mt-[1px] text-[11px] font-medium text-[#9A9AA4]">
              {playerCount} {live ? 'new rivals' : 'players'} · {roundLabel}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9.5px] font-bold uppercase tracking-wide text-[#8A8A92]">
              Tier {tier + 1} / {tierCount}
            </div>
            <div className="mt-[6px] flex justify-end gap-[3px]">
              {CONFIG.TIERS.map((_, i) => (
                <span
                  key={i}
                  className="h-[6px] w-[6px] rounded-[2px]"
                  style={{
                    background: i < tier ? '#E3A82B' : i === tier ? '#FFFFFF' : 'rgba(255,255,255,0.18)'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {provisional && (
          <LiveProvisionalBanner
            settled={liveProgress!.settled}
            total={liveProgress!.total}
            className="mt-[13px]"
          />
        )}

        {/* my live standing */}
        <div className="mt-[13px] flex items-center gap-[14px] rounded-[16px] bg-white/[0.06] px-[15px] py-[13px]">
          <div className="text-center">
            <div className="num text-[34px] font-extrabold leading-none tracking-title text-white">
              {myRank ?? '—'}
              {myRank != null && <span className="text-[15px] font-bold text-[#8A8A92]">{ordinalSuffix(myRank)}</span>}
            </div>
            <div className="mt-[5px] text-[10px] font-semibold text-[#8A8A92]">of {playerCount}</div>
          </div>
          <div className="h-[40px] w-px bg-white/10" />
          <div className="min-w-0 flex-1">
            <span
              className={cn(
                'inline-flex items-center gap-[5px] rounded-full px-[10px] py-[4px] text-[10.5px] font-extrabold uppercase tracking-wide',
                status.tone === 'up' && 'bg-accent/20 text-accent',
                status.tone === 'down' && 'bg-danger/20 text-[#FF8A86]',
                status.tone === 'hold' && 'bg-white/10 text-[#C9C9D2]'
              )}
            >
              {status.tone === 'up' && <ChevronUp width={11} height={11} strokeWidth={3.4} />}
              {status.tone === 'down' && <ChevronDown width={11} height={11} strokeWidth={3.4} />}
              {status.tone === 'hold' && <Minus width={11} height={11} strokeWidth={3.4} />}
              {status.label}
            </span>
            <div className="mt-[6px] text-[11.5px] font-medium leading-snug text-[#9A9AA4]">{status.sub}</div>
          </div>
          <div className="shrink-0 text-right">
            <div className="num text-[24px] font-extrabold leading-none text-accent">
              {myScore != null ? signedPoints(myScore) : '—'}
            </div>
            <div className="mt-[5px] text-[10px] font-semibold text-[#8A8A92]">your pts</div>
          </div>
        </div>

        {/* compact stakes — full table below carries the cut lines */}
        {(promoteCount > 0 || relegateCount > 0) && (
          <p className="mt-[10px] text-[11px] font-medium leading-[1.45] text-[#8A8A92]">
            {promoteCount > 0 && !atTop && `Top ${promoteCount} promote to ${up}`}
            {promoteCount > 0 && !atTop && relegateCount > 0 && !atBottom && ' · '}
            {relegateCount > 0 && !atBottom && `Bottom ${relegateCount} drop to ${down}`}
            {relegateCount > 0 && atBottom && `Bottom ${relegateCount} still relegate within Bronze`}
          </p>
        )}
      </div>
    </div>
  );
}

function statusFor(
  zone: Zone | null,
  movement: Movement | null
): { tone: 'up' | 'down' | 'hold'; label: string; sub: string } {
  if (movement === 'PROMOTED') return { tone: 'up', label: 'Promoted', sub: 'You finished in the promotion zone' };
  if (movement === 'RELEGATED') return { tone: 'down', label: 'Relegated', sub: 'You finished in the drop zone' };
  if (movement === 'STAYED') return { tone: 'hold', label: 'Held tier', sub: 'You held your division this round' };
  if (zone === 'promotion') return { tone: 'up', label: 'Promotion spot', sub: 'Stay here to climb a tier' };
  if (zone === 'relegation') return { tone: 'down', label: 'Drop zone', sub: 'Climb out to avoid relegation' };
  if (zone === 'middle') return { tone: 'hold', label: 'Holding', sub: 'Push up for a promotion spot' };
  return { tone: 'hold', label: 'No score yet', sub: 'Build cards to join this board' };
}

/**
 * An amber "this ranking isn't final yet" banner for a live round: a pulsing dot,
 * a LIVE tag, and how many of the round's matches have finished. Sits on the dark
 * standing surfaces (division hero + the Play-tab strip) so a player who's top of
 * the live board knows more results are still to come — the exact trap that makes a
 * provisional lead read as a settled one.
 */
export function LiveProvisionalBanner({
  settled,
  total,
  className
}: {
  settled: number;
  total: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-[7px] rounded-[12px] border border-[rgba(240,179,74,0.34)] bg-[rgba(240,179,74,0.12)] px-[11px] py-[8px]',
        className
      )}
    >
      <span className="relative flex h-[7px] w-[7px] shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F0B34A] opacity-60" />
        <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#F0B34A]" />
      </span>
      <span className="text-[10.5px] font-extrabold uppercase tracking-wide text-[#F0B34A]">Live</span>
      <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-[#E7B765]">
        {settled}/{total} {total === 1 ? 'match' : 'matches'} in · not final until the round ends
      </span>
    </div>
  );
}

export function TierDot({ tierIndex }: { tierIndex: number }) {
  const colors = ['#C58A4E', '#9AA3AE', '#E3A82B', '#9FB2CC', '#5AA6E6', '#7E5BD6'];
  return (
    <span
      className="h-[7px] w-[7px] rounded-[2px]"
      style={{ background: colors[tierIndex] ?? colors[0] }}
    />
  );
}

export function TierPill({ tierIndex }: { tierIndex: number }) {
  return (
    <span className="inline-flex items-center gap-[6px] rounded-full border border-[#E3A82B]/35 bg-[#E3A82B]/15 px-[11px] py-[5px]">
      <TierDot tierIndex={tierIndex} />
      <span className="text-[11px] font-extrabold tracking-wide text-[#E8C25E]">
        {cap(tierName(tierIndex)).toUpperCase()} TIER
      </span>
    </span>
  );
}
